import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Button,
  Grid,
  Alert
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import SubscriberBadge from '../components/common/SubscriberBadge';
import { useParams, useNavigate } from 'react-router-dom';
import FixedBottomBar from '../components/common/FixedBottomBar';
import { DndContext, closestCenter, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy
} from '@dnd-kit/sortable';
import {
  GameList,
  GameListItem,
  updateGameList,
  reorderGameListItems,
  removeGameFromList,
  addGameToList,
  getGameListDetail
} from '../services/contents_api';
import { SortableGameItem } from '../components/GameList/SortableGameItem';
import GameSelectDialog, { GameSelectResult } from '../components/dialogs/GameSelectDialog';
import { useUser } from '../contexts/UserContext';
import { isSubscribed } from '../utils/subscription';

const EditGameListPage: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const hasPaidPlan = isSubscribed(user?.subscription_plan ?? 0);
  const isDevelopment = process.env.NODE_ENV === 'development';
  const MAX_ITEMS = isDevelopment ? 100 : 60;
  const [list, setList] = useState<GameList | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState(0);
  const [items, setItems] = useState<GameListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [listType, setListType] = useState(1);
  const [gameSelectOpen, setGameSelectOpen] = useState(false);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchList = async () => {
      try {
        const data = await getGameListDetail(Number(listId));
        setList(data);
        setTitle(data.title);
        setDescription(data.description);
        setVisibility(data.visibility);
        setListType(data.list_type);
        setItems(data.items);
      } catch (error) {
        setError('リストの読み込みに失敗しました');
        console.error(error);
      }
    };
    if (listId) {
      fetchList();
    }
  }, [listId]);

  const handleSave = async () => {
    try {
      if (!listId) return;

      await updateGameList(Number(listId), {
        title,
        description,
        visibility,
        list_type: listType
      });

      if (items.length > 0) {
        await reorderGameListItems(Number(listId), items);
      }

      navigate(`/list/${listId}`);
    } catch (error) {
      setError('リストの更新に失敗しました');
      console.error(error);
    }
  };

  const handleRemoveGame = async (gameId: number) => {
    try {
      if (!listId) return;
      await removeGameFromList(Number(listId), gameId);
      setItems(items.filter(item => item.game_id !== gameId));
    } catch (error) {
      setError('ゲームの削除に失敗しました');
      console.error(error);
    }
  };

  const handleAddGamesFromDialog = async (games: GameSelectResult[]) => {
    try {
      if (!listId) return;

      if (items.length + games.length > MAX_ITEMS) {
        setError(`リストに追加できるゲームは最大${MAX_ITEMS}件までです`);
        return;
      }

      for (const game of games) {
        await addGameToList(Number(listId), game.game_id);
      }

      const updatedList = await getGameListDetail(Number(listId));
      setItems(updatedList.items);
    } catch (error) {
      setError('ゲームの追加に失敗しました');
      console.error(error);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const existingGameIds = useMemo(() => {
    return items.map(item => item.game_id);
  }, [items]);

  if (!list) {
    return <Container><Typography>読み込み中...</Typography></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* ヘッダー */}
      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
        リストを編集
      </Typography>

      {/* リスト設定 */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="タイトル"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            size="small"
          />
          <TextField
            label="説明"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>リストタイプ</InputLabel>
              <Select
                value={listType}
                label="リストタイプ"
                onChange={(e) => setListType(Number(e.target.value))}
              >
                <MenuItem value={1}>コレクション</MenuItem>
                <MenuItem value={2}>ランキング</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={!hasPaidPlan}>
              <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  公開設定
                  <SubscriberBadge variant="inline" disabled={!hasPaidPlan} />
                </Box>
              </InputLabel>
              <Select
                value={hasPaidPlan ? visibility : 1}
                label="公開設定　　　"
                onChange={(e) => setVisibility(Number(e.target.value))}
              >
                <MenuItem value={1}>公開</MenuItem>
                <MenuItem value={0}>非公開</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* ゲームリスト */}
      <Box sx={{ mb: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ゲーム
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {items.length}/{MAX_ITEMS}
          </Typography>
          {items.length < MAX_ITEMS && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setGameSelectOpen(true)}
              size="small"
              sx={{
                color: 'text.secondary',
                backgroundColor: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 400,
                fontSize: '0.7rem',
                px: 1.2,
                py: 0.4,
                minHeight: 0,
                lineHeight: 1.4,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiButton-startIcon': {
                  mr: 0.3,
                },
              }}
            >
              追加
            </Button>
          )}
        </Box>

        {items.length >= MAX_ITEMS && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            上限に達しました。入れ替える場合は既存のタイトルを削除してください。
          </Typography>
        )}

        {items.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            長押しでドラッグして並び替え
          </Typography>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[
            (args) => ({
              ...args,
              x: args.transform.x,
              y: args.transform.y,
              scaleX: 1,
              scaleY: 1,
            })
          ]}
        >
          <SortableContext
            items={items.map(item => item.id)}
            strategy={rectSortingStrategy}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(3, 1fr)',
                  sm: 'repeat(4, 1fr)',
                  md: 'repeat(6, 1fr)'
                },
                gap: 1.5
              }}
            >
              {items.map((item) => (
                <Box key={item.id} sx={{ position: 'relative' }}>
                  <SortableGameItem item={item} />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveGame(item.game_id)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      '&:hover': {
                        bgcolor: 'error.main',
                        color: 'white'
                      },
                      padding: '3px'
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>

      <GameSelectDialog
        open={gameSelectOpen}
        onClose={() => setGameSelectOpen(false)}
        onMultiSelect={handleAddGamesFromDialog}
        title="リストにゲームを追加"
        excludeGameIds={existingGameIds}
        showAllSearchOption
        multiSelect
        maxSelect={MAX_ITEMS - items.length}
      />

      <FixedBottomBar>
        <Button
          variant="outlined"
          onClick={() => navigate(`/profile?tab=lists`)}
          size="small"
          sx={{
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': { borderColor: 'text.secondary' },
            minWidth: 100,
          }}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          size="small"
          sx={{ minWidth: 100 }}
        >
          保存
        </Button>
      </FixedBottomBar>
    </Container>
  );
};

export default EditGameListPage;
